import { Button, CameraStreamModel, Device, Dialog, Facility, facilityToDevice, GroundCalibrationPair, heightFitMismatch, IntrinsicsTranslator, intrinsicsAnglesForDevice, placementFromPoseFit, useImageCoordTranslator, Vector2, Vector3 } from "@app/shared";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getFacilityCoordsHelper, toastError } from "../../../helpers";
import { useFacilityCenter } from "../../../helpers/mapHelpers";
import { DeviceSnapshotState, useDeviceSnapshot } from "../../../hooks/useDeviceSnapshot";
import { HomographyImageEditor } from "../DeviceHomographyImageEditor";
import { DynamicFixedSizeContainer } from "../DynamicFixedSizeContainer";
import { NumericInput } from "../inputs";
import { DeviceMapShape, OmniGoogleMap, OmniMapMethods } from "../maps";

/** Everything the reference-point fit determines, in stored Device units. */
export type ReferenceAlignmentResult = {
    positionX: number;
    positionY: number;
    headingDegrees: number;
    height: number;
    /** Stored convention: negative = looking down, radians. */
    fusionOffsetPitch: number;
    fusionOffsetRoll: number;
    /** Kept in lockstep with the fitted pitch (positive-down degrees) for legacy consumers. */
    desiredTiltDegrees: number;
};

type DeviceReferenceAlignmentDialogProps = {
    open: boolean;
    device: Device | undefined;
    facility: Facility;
    onCancel: () => void;
    /** OK pressed: apply the aligned pose to the layout (persisted by the layout's own Save). */
    onApply: (result: ReferenceAlignmentResult) => void;
    /** Supplies the snapshot from somewhere other than CoreApi — see DeviceCameraView. */
    snapshotOverride?: DeviceSnapshotState;
};

type WorkingPose = { positionX: number; positionY: number; headingDegrees: number; heightM: number; pitchDeg: number; rollDeg: number };
type PairUi = { pixel: Vector2; latLng: google.maps.LatLngLiteral };

const normalizeHeading = (deg: number) => ((deg % 360) + 360) % 360;
const round = (value: number, decimals: number) => Number(value.toFixed(decimals));

/**
 * Full-screen "Reference Points" device alignment: place the unit on the map (if it isn't
 * yet), click matching camera↔map point pairs, and fit the full placement + camera pose
 * (position, heading, height, pitch, roll) with the exact (unregularized) reprojection fit.
 * Every fit auto-applies to the working pose — the camera projections, residuals, and the
 * device marker all update live; OK hands the pose to the layout editor and the layout's
 * regular Save persists it to the device.
 */
export const DeviceReferenceAlignmentDialog: FC<DeviceReferenceAlignmentDialogProps> = ({ open, device, facility, onCancel, onApply, snapshotOverride }) => {
    const resolvedSnapshot = useDeviceSnapshot(!snapshotOverride && open ? device : undefined);
    const snapshot = snapshotOverride ?? resolvedSnapshot;
    const handleRequestNewPhoto = useCallback(async () => {
        try {
            await snapshot.requestNewPhoto();
        } catch (e) {
            toastError("Unable to request a photo", e instanceof Error ? e.message : String(e));
        }
    }, [snapshot]);
    const [imgSize, setImgSize] = useState({ width: 640, height: 480 });
    const [clientSize, setClientSize] = useState({ width: 640, height: 480 });

    const [pose, setPose] = useState<WorkingPose>({ positionX: 0, positionY: 0, headingDegrees: 0, heightM: 0, pitchDeg: 0, rollDeg: 0 });
    const [positionPlaced, setPositionPlaced] = useState(false);
    const [pairs, setPairs] = useState<PairUi[]>([]);
    const [pendingPixel, setPendingPixel] = useState<Vector2 | null>(null);
    const [lastFitMeanPx, setLastFitMeanPx] = useState<number | null>(null);
    const [fittedHeightM, setFittedHeightM] = useState<number | null>(null);
    const [cameraModel, setCameraModel] = useState<CameraStreamModel>("dewarped2716N");
    const [hoverRangeMeters, setHoverRangeMeters] = useState<number | null>(null);

    const pairMarkers = useRef<google.maps.Marker[]>([]);
    const projectionDots = useRef<google.maps.Circle[]>([]);
    const map = useRef<OmniMapMethods | null>(null);

    // Seed the working pose from the device each time the dialog opens.
    useEffect(() => {
        if (!open || !device) return;
        const { pitchDeg, rollDeg } = intrinsicsAnglesForDevice(device);
        const px = device.positionX ?? 0;
        const py = device.positionY ?? 0;
        setPose({ positionX: px, positionY: py, headingDegrees: normalizeHeading(device.headingDegrees ?? 0), heightM: device.height ?? 0, pitchDeg, rollDeg });
        setPositionPlaced(!(px === 0 && py === 0));
        setPairs([]);
        setPendingPixel(null);
        setLastFitMeanPx(null);
        setFittedHeightM(null);
        setHoverRangeMeters(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, device?.id]);

    /** The device as currently aligned — drives the projections, residuals, and the map marker. */
    const candidateDevice = useMemo(() => {
        if (!device) return undefined;
        return Object.assign(Object.create(Object.getPrototypeOf(device)), device, {
            positionX: pose.positionX,
            positionY: pose.positionY,
            headingDegrees: pose.headingDegrees,
            height: pose.heightM,
            fusionOffsetPitch: pose.pitchDeg * (-Math.PI / 180),
            fusionOffsetRoll: pose.rollDeg * (Math.PI / 180),
            // The map FOV cone and boresight are drawn from desiredTiltDegrees, so without this the
            // preview keeps the device's pre-fit tilt while position, heading and height are fitted —
            // a cone that does not describe where the fitted camera looks. Same negation handleOk uses.
            desiredTiltDegrees: -pose.pitchDeg,
        }) as Device;
    }, [device, pose]);

    const translator = useImageCoordTranslator(candidateDevice, imgSize.width, imgSize.height, cameraModel);
    const coordsHelper = useMemo(() => getFacilityCoordsHelper(facility), [facility]);
    const [centerCoords, centerIsLoading] = useFacilityCenter(facility);

    const hoverDot = useMemo(() => new google.maps.Circle({ fillOpacity: 1.0, fillColor: "red", strokeOpacity: 1.0, strokeColor: "red", strokeWeight: 1.0, radius: 1, clickable: false }), []);

    const latLngToDeviceGround = useCallback(
        (latLng: google.maps.LatLngLiteral): Vector3 | undefined => {
            if (!coordsHelper) return undefined;
            const facilityPt = coordsHelper.fromLatLng(latLng);
            const [devicePt] = facilityToDevice([facilityPt], { x: pose.positionX, y: pose.positionY }, pose.headingDegrees);
            return new Vector3({ x: devicePt.x, y: devicePt.y, z: 0 });
        },
        [coordsHelper, pose.positionX, pose.positionY, pose.headingDegrees],
    );

    const handleMouseMove = useCallback(
        (point: [number, number] | undefined) => {
            const hide = () => {
                hoverDot.setVisible(false);
                setHoverRangeMeters(null);
            };
            if (!point || !positionPlaced || !translator.isCalibrated || !coordsHelper || !map.current?.map) {
                hide();
                return;
            }
            const pixel = new Vector2({ x: point[0], y: point[1] });
            const ground = translator.imagePixelsToGround(pixel);
            if (!Number.isFinite(ground.x) || !Number.isFinite(ground.y)) {
                hide();
                return;
            }
            const reprojected = translator.groundToImagePixels(new Vector3({ x: ground.x, y: ground.y, z: 0 }));
            if (!Number.isFinite(reprojected.x) || !Number.isFinite(reprojected.y) || Math.hypot(reprojected.x - pixel.x, reprojected.y - pixel.y) > imgSize.width * 0.015) {
                hide();
                return;
            }
            hoverDot.setCenter(coordsHelper.toLatLng(new Vector2({ x: ground.x, y: ground.y })));
            hoverDot.setVisible(true);
            hoverDot.setMap(map.current.map);
            setHoverRangeMeters(Math.hypot(ground.x - pose.positionX, ground.y - pose.positionY));
        },
        [positionPlaced, translator, coordsHelper, hoverDot, imgSize.width, pose.positionX, pose.positionY],
    );

    const handleImageClick = useCallback(
        (point: [number, number]) => {
            if (!open || !positionPlaced) return;
            setPendingPixel(new Vector2({ x: (point[0] / imgSize.width) * 640, y: (point[1] / imgSize.height) * 480 }));
        },
        [open, positionPlaced, imgSize.width, imgSize.height],
    );

    const handleMapClick = useCallback(
        (pos: google.maps.LatLng | null) => {
            if (!open || !pos || !coordsHelper) return;
            const latLng = pos.toJSON();
            if (!positionPlaced) {
                const fac = coordsHelper.fromLatLng(latLng);
                setPose((p) => ({ ...p, positionX: fac.x, positionY: fac.y }));
                setPositionPlaced(true);
                return;
            }
            if (!pendingPixel) return;
            setPairs((curr) => [...curr, { pixel: pendingPixel, latLng }]);
            setPendingPixel(null);
        },
        [open, coordsHelper, positionPlaced, pendingPixel],
    );

    // Per-pair residuals under the current pose: px (640x480 basis) + ground meters.
    const currentErrors = useMemo(() => {
        if (!candidateDevice) return [];
        const t = new IntrinsicsTranslator();
        t.calculateFromParameters(false, pose.heightM > 0 ? pose.heightM : 2, pose.pitchDeg, pose.rollDeg, candidateDevice.fusionEstimatedReturnHeight ?? 0.8, cameraModel);
        return pairs.map((pair) => {
            const ground = latLngToDeviceGround(pair.latLng);
            if (!ground) return undefined;
            const projected = t.groundToImage(ground);
            if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y)) return undefined;
            const errPx = Math.hypot(projected.x - pair.pixel.x, projected.y - pair.pixel.y);
            const displayPx = new Vector2({ x: (pair.pixel.x / 640) * imgSize.width, y: (pair.pixel.y / 480) * imgSize.height });
            const projectedGround = translator.imagePixelsToGround(displayPx);
            const clickedFacility = coordsHelper?.fromLatLng(pair.latLng);
            const errM = clickedFacility && Number.isFinite(projectedGround.x) ? Math.hypot(projectedGround.x - clickedFacility.x, projectedGround.y - clickedFacility.y) : undefined;
            return { errPx, errM };
        });
    }, [candidateDevice, pairs, pose, cameraModel, latLngToDeviceGround, translator, coordsHelper, imgSize.width, imgSize.height]);

    // Numbered markers for the clicked map truths.
    useEffect(() => {
        pairMarkers.current.forEach((m) => m.setMap(null));
        pairMarkers.current = [];
        if (!open || !map.current?.map) return;
        pairMarkers.current = pairs.map(
            (pair, i) => new google.maps.Marker({ position: pair.latLng, map: map.current!.map, label: { text: String(i + 1), color: "white" }, title: `Reference point ${i + 1}` }),
        );
        return () => {
            pairMarkers.current.forEach((m) => m.setMap(null));
            pairMarkers.current = [];
        };
    }, [open, pairs]);

    // Orange dots: where each clicked camera pixel lands under the current pose.
    useEffect(() => {
        projectionDots.current.forEach((c) => c.setMap(null));
        projectionDots.current = [];
        if (!open || !coordsHelper || !translator.isCalibrated || !map.current?.map) return;
        projectionDots.current = pairs.map((pair) => {
            const displayPx = new Vector2({ x: (pair.pixel.x / 640) * imgSize.width, y: (pair.pixel.y / 480) * imgSize.height });
            const ground = translator.imagePixelsToGround(displayPx);
            return new google.maps.Circle({
                center: coordsHelper.toLatLng(new Vector2({ x: ground.x, y: ground.y })),
                radius: 1,
                fillColor: "orange",
                fillOpacity: 1.0,
                strokeColor: "orange",
                strokeOpacity: 1.0,
                strokeWeight: 1.0,
                clickable: false,
                map: map.current!.map,
            });
        });
        return () => {
            projectionDots.current.forEach((c) => c.setMap(null));
            projectionDots.current = [];
        };
    }, [open, pairs, translator, coordsHelper, imgSize.width, imgSize.height]);

    // Fit the full placement + pose and apply it immediately — no separate "use values" step.
    const handleFit = () => {
        if (!candidateDevice) return;
        const fitPairs: GroundCalibrationPair[] = [];
        for (const pair of pairs) {
            const ground = latLngToDeviceGround(pair.latLng);
            if (ground) fitPairs.push({ pixel: pair.pixel, ground });
        }
        if (fitPairs.length < 3) return;

        const result = new IntrinsicsTranslator().fitPoseFromGroundPairs(
            fitPairs,
            { pitchDeg: pose.pitchDeg, rollDeg: pose.rollDeg, heightM: pose.heightM > 0 ? pose.heightM : 6 },
            {
                cameraModel,
                estimatedReturnHeight: candidateDevice.fusionEstimatedReturnHeight ?? 0.8,
                fitHeading: true,
                fitPosition: true,
                regularize: false,
            },
        );

        const placement = placementFromPoseFit(result, { positionX: pose.positionX, positionY: pose.positionY, headingDegrees: pose.headingDegrees });
        setPose({
            positionX: placement.positionX,
            positionY: placement.positionY,
            headingDegrees: placement.headingDegrees,
            heightM: result.heightM,
            pitchDeg: result.pitchDeg,
            rollDeg: result.rollDeg,
        });
        setLastFitMeanPx(result.meanErrorPx);
        setFittedHeightM(result.heightM);
    };

    const heightMismatch = heightFitMismatch(fittedHeightM, device?.height);

    // Dragging the device marker or its heading arrow adjusts the working pose directly, so the
    // mount spot and boresight can be set by eye before (or instead of) fitting from point pairs.
    // Any previous fit error no longer describes this pose, so drop it rather than leave a stale
    // number next to a hand-moved device.
    const handleDeviceMarkerUpdate = useCallback((updated: Device) => {
        setPose((p) => ({
            ...p,
            positionX: updated.positionX ?? p.positionX,
            positionY: updated.positionY ?? p.positionY,
            headingDegrees: normalizeHeading(updated.headingDegrees ?? p.headingDegrees),
        }));
        setLastFitMeanPx(null);
        setFittedHeightM(null);
    }, []);

    // Typing a pose value applies it straight to the working pose, so the camera projection,
    // residuals and device marker update live — the point of checking against the image is being
    // able to correct what looks wrong. Same staleness rule as dragging: drop the old fit error.
    const setPoseField = useCallback(
        (key: keyof WorkingPose) => (value?: number) => {
            if (value == null || !Number.isFinite(value)) return;
            setPose((p) => ({ ...p, [key]: value }));
            setLastFitMeanPx(null);
        },
        [],
    );

    // Start over on placement: position/tilt/heading go to 0 and the map asks for the mount spot again.
    const handleResetPosition = () => {
        setPose((p) => ({ ...p, positionX: 0, positionY: 0, headingDegrees: 0, pitchDeg: 0 }));
        setPositionPlaced(false);
        setPendingPixel(null);
        setLastFitMeanPx(null);
    };

    const handleClearPairs = () => {
        setPairs([]);
        setPendingPixel(null);
        setLastFitMeanPx(null);
    };

    const handleOk = () => {
        onApply({
            positionX: pose.positionX,
            positionY: pose.positionY,
            headingDegrees: normalizeHeading(pose.headingDegrees),
            height: pose.heightM,
            fusionOffsetPitch: pose.pitchDeg * (-Math.PI / 180),
            fusionOffsetRoll: pose.rollDeg * (Math.PI / 180),
            // Negated to match fusionOffsetPitch: both store negative for looking down. Devices in
            // the field carry negative tilts (-13, -8), and CoreApi's FOV rendering only produces a
            // near edge closer than its far edge for negative values. Writing +pitchDeg here flipped
            // the sign of a firmware-facing field on every fit.
            desiredTiltDegrees: -pose.pitchDeg,
        });
    };

    const statusText = !positionPlaced
        ? "Step 1 — click the spot on the map where this unit is mounted. A rough spot is fine; the fit refines it."
        : pendingPixel
          ? `Now click that same real-world spot on the map to place point ${pairs.length + 1}.`
          : "Click a recognizable ground feature on the camera image (crosswalk corner, curb edge, stop bar, pole base).";

    const markerR = Math.max(6, imgSize.width * 0.009);

    return (
        <Dialog show={open} title={`Align ${device?.name ?? "Device"} — Reference Points`} okButtonText="OK" disableOkButton={!positionPlaced} onOkClick={handleOk} onCancelClick={onCancel} closeOnOutsideClick={false} className="w-[97vw] max-w-[1800px]">
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <select value={cameraModel} onChange={(e) => setCameraModel(e.target.value as CameraStreamModel)} className="px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" title="How this camera's pixels are projected. Orin devices dewarp the stream on-device (straight lines stay straight at the frame edges).">
                    <option value="dewarped2716N">Dewarped 2716N (Orin)</option>
                    <option value="dewarped2701L">Dewarped 2701L (Orin)</option>
                    <option value="raw2716N">Raw 2716N (distorted stream)</option>
                </select>
                {snapshot.canRequestNewPhoto && (
                    <Button onClick={handleRequestNewPhoto} disabled={snapshot.isRequesting}>
                        {snapshot.isRequesting ? "Waiting for photo…" : "New Photo"}
                    </Button>
                )}
                {snapshot.source && (
                    <span className="text-xs text-gray-500" title="Target photos come from the analytics stream (dewarped on Orin). Device photos come from the photo-command capture, which on older firmware can be the raw pre-dewarp camera or a differently framed still — calibration pixels must match the analytics stream's framing.">
                        {snapshot.source === "device-photo" ? "Showing: device photo" : "Showing: last vehicle photo (analytics stream)"} · {imgSize.width}×{imgSize.height}
                    </span>
                )}
                <Button onClick={handleResetPosition}>Reset Position</Button>
                {pairs.length > 0 && <Button onClick={handleClearPairs}>Clear Points</Button>}
                <Button onClick={handleFit} disabled={!positionPlaced || pairs.length < 3} title={pairs.length < 3 ? "Add at least 3 point pairs (4-6 recommended)" : undefined}>
                    Fit
                </Button>
                <span className="text-sm font-medium text-brand-primary ms-auto">{statusText}</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <div className="text-center">
                    <HomographyImageEditor srcImageUrl={snapshot.url} facility={facility} editMode="none" onMouseMove={handleMouseMove} onImageClick={handleImageClick} onImgSizeChange={setImgSize} onClientSizeChange={setClientSize}>
                        {pairs.map((pair, i) => {
                            const cx = (pair.pixel.x / 640) * imgSize.width;
                            const cy = (pair.pixel.y / 480) * imgSize.height;
                            return (
                                <g key={i}>
                                    <circle cx={cx} cy={cy} r={markerR} fill="#16a34a" fillOpacity={0.85} stroke="white" strokeWidth={markerR * 0.2} />
                                    <text x={cx} y={cy} fill="white" fontSize={markerR * 1.2} fontWeight="bold" textAnchor="middle" dominantBaseline="central">
                                        {i + 1}
                                    </text>
                                </g>
                            );
                        })}
                        {pendingPixel && <circle cx={(pendingPixel.x / 640) * imgSize.width} cy={(pendingPixel.y / 480) * imgSize.height} r={markerR} fill="none" stroke="#16a34a" strokeWidth={markerR * 0.35} strokeDasharray={`${markerR * 0.6} ${markerR * 0.4}`} />}
                    </HomographyImageEditor>
                    <p className="text-sm text-gray-600 h-5">{hoverRangeMeters != null ? `${hoverRangeMeters.toFixed(1)} m from the device` : positionPlaced ? "Hover the image to preview where a pixel lands on the map" : ""}</p>
                </div>

                <div className="text-center">
                    <DynamicFixedSizeContainer getWidth={() => clientSize.width} getHeight={() => clientSize.height} className="mt-5 mx-3" margin={{ right: 16, left: 0, top: 0, bottom: 0 }}>
                        {({ width, height }) => {
                            return (
                                centerCoords &&
                                !centerIsLoading && (
                                    <OmniGoogleMap ref={map} center={centerCoords} width={width} height={height} fitToPolygons={true} onClick={handleMapClick} draggableCursor={!positionPlaced || pendingPixel ? "crosshair" : undefined}>
                                        {positionPlaced && candidateDevice && <DeviceMapShape shape={candidateDevice} facility={facility} isEditable={true} onUpdate={handleDeviceMarkerUpdate} />}
                                    </OmniGoogleMap>
                                )
                            );
                        }}
                    </DynamicFixedSizeContainer>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="text-sm text-gray-700 border border-gray-300 rounded-md p-3">
                    <h2 className="font-semibold mb-1">How to get a good fit</h2>
                    <ol className="list-decimal ms-5 space-y-0.5">
                        <li>Place the unit on the map (roughly is fine), then pair up points: click a feature on the camera, then the same feature on the map.</li>
                        <li>Use ground-level features only — paint lines, curb corners, stop bars. Avoid rooftops and tall objects (aerial imagery shifts them).</li>
                        <li>Add 4–6 pairs, spread out: some near, some far, some left, some right of the view.</li>
                        <li>Press Fit — position, heading, height, pitch, and roll update automatically. Orange dots show where your camera points currently land; they should sit on the green numbered pins.</li>
                        <li>A mean error under ~10 px is a good fit. Remove a suspect point (✕) and refit if one stands out. Then press OK, and Save the layout to store it on the device.</li>
                    </ol>
                </div>

                <div className="text-sm text-gray-700 border border-gray-300 rounded-md p-3">
                    <h2 className="font-semibold mb-1">Alignment values{lastFitMeanPx != null ? ` — mean error ${lastFitMeanPx.toFixed(1)} px` : ""}</h2>
                    {heightMismatch && (
                        <p className="text-amber-700 mb-2">
                            The fit put the camera at {round(heightMismatch.fitted, 2)} m, {heightMismatch.percent}% off the {round(heightMismatch.measured, 2)} m on record. If you measured the mount, trust the tape: spread your points over more range and refit, or set the height by hand and fit again.
                        </p>
                    )}
                    <div className="grid grid-cols-3 gap-x-4 gap-y-1 items-center">
                        <span>Position X</span>
                        <span className="col-span-2 flex items-center gap-1">{positionPlaced ? <NumericInput className="w-24 py-0.5" value={round(pose.positionX, 2)} onChange={setPoseField("positionX")} /> : "not placed"}{positionPlaced && <span className="text-gray-500">m</span>}</span>
                        <span>Position Y</span>
                        <span className="col-span-2 flex items-center gap-1">{positionPlaced ? <NumericInput className="w-24 py-0.5" value={round(pose.positionY, 2)} onChange={setPoseField("positionY")} /> : "not placed"}{positionPlaced && <span className="text-gray-500">m</span>}</span>
                        <span>Heading</span>
                        <span className="col-span-2 flex items-center gap-1">
                            <NumericInput className="w-24 py-0.5" value={round(normalizeHeading(pose.headingDegrees), 1)} onChange={setPoseField("headingDegrees")} />
                            <span className="text-gray-500">°</span>
                        </span>
                        <span>Height</span>
                        <span className="col-span-2 flex items-center gap-1">
                            <NumericInput className="w-24 py-0.5" value={round(pose.heightM, 2)} onChange={setPoseField("heightM")} />
                            <span className="text-gray-500">m</span>
                        </span>
                        <span>Pitch (down)</span>
                        <span className="col-span-2 flex items-center gap-1">
                            <NumericInput className="w-24 py-0.5" value={round(pose.pitchDeg, 1)} onChange={setPoseField("pitchDeg")} />
                            <span className="text-gray-500">°</span>
                        </span>
                        <span>Roll</span>
                        <span className="col-span-2 flex items-center gap-1">
                            <NumericInput className="w-24 py-0.5" value={round(pose.rollDeg, 1)} onChange={setPoseField("rollDeg")} />
                            <span className="text-gray-500">°</span>
                        </span>
                    </div>
                    {pairs.length > 0 && (
                        <ul className="mt-2 border-t border-gray-200 pt-1">
                            {pairs.map((pair, i) => (
                                <li key={i} className="flex items-center gap-3 py-0.5">
                                    <span className="font-semibold">#{i + 1}</span>
                                    <span>
                                        camera ({pair.pixel.x.toFixed(0)}, {pair.pixel.y.toFixed(0)})
                                    </span>
                                    <span className="text-gray-500">{currentErrors[i] ? `error ${currentErrors[i]!.errPx.toFixed(1)} px${currentErrors[i]!.errM != null ? ` (~${currentErrors[i]!.errM!.toFixed(1)} m)` : ""}` : "no projection"}</span>
                                    <button className="text-red-600 hover:text-red-800" title="Remove this point pair" onClick={() => setPairs((curr) => curr.filter((_, idx) => idx !== i))}>
                                        ✕
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </Dialog>
    );
};
